import React, { useState, useCallback, useEffect } from "react";
import { Form, Row, Col, Flex, Typography, notification, Spin } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { Button } from "components/Common/Button";
import alpha from "helpers/alpha";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import { FormSelect } from "components/Common/Form/FormSelect";
import { FormTagSelect } from "components/Common/Form/FormTagSelect";
import {
	useGetDomainGymQuery,
	useGetCategoriesQuery,
	useGetCoachesQuery,
	useGetMuxAuthenticatedUrlMutation,
	useEditWorkoutMutation,
	useGetMuxVideoUrlQuery,
	useGetGymWorkoutsQuery,
	useGetGymEquipmentQuery,
	useAddGymEquipmentMutation,
	useCreateCoachMutation,
} from "store/api/api";
import { useParams } from "react-router";
import { FormInput } from "components/Common/Form/FormInput";
import { validateRequiredEncodedLength, validateEncodedLength } from "helpers/Form/commonFormValidations";
import { FormTextArea } from "components/Common/Form/FormTextArea";
import { useSelector } from "react-redux";
import { selectSelectedWorkout } from "store/selectors";
import "./styles.scss";
import { decodeString, encodeString, sortByProperty, validEquipments, validTrainers } from "utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import DateTimeTimezone from "components/DateTimeTimezone";
import MuxUploadAndVideoPlayer from "components/Forms/Workout/UploadAssetsForm.jsx/MuxUploadAndPlayer";
import { EditOutlined, UploadOutlined } from "@ant-design/icons";
import VideoPlayer from "components/UI/VideoPlayer/VideoPlayer";
import ImageUpload from "components/ImageUpload";
import { maxImageAssetSize, imageSizeLimitError, durationOptions } from "helpers/constants";

dayjs.extend(utc);
dayjs.extend(timezone);

const WorkoutPreview = ({ values, nextStep, previousStep, loading, preRecordedLive }) => {
	const primaryColor = usePrimaryColor();
	const [form] = Form.useForm();
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const getWorkoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });
	const currentWorkout = getWorkoutsResponse?.data?.workouts?.find((workout) => workout.id === values?.workout_id);
	const { data: categoriesData } = useGetCategoriesQuery(domainGymId, {
		skip: !domainGymId,
	});
	const {
		data: coachesData,
		isLoading: isCoachesLoading,
		refetch: refetchTrainers,
	} = useGetCoachesQuery(domainGymId, {
		skip: !domainGymId,
	});
	const { data: equipments, refetch: refetchEquipment } = useGetGymEquipmentQuery(domainGymId, {
		skip: !domainGymId,
	});
	const [currentDatetime, setCurrentDatetime] = useState(null);
	const [isDisabledSaveBtn, setDisabledSaveBtn] = useState(false);
	const selectedWorkout = useSelector(selectSelectedWorkout);
	const [thumbnailFile, setThumbnailFile] = useState(null);
	const uploaderRef = React.useRef(null);
	const [editWorkoutTrigger] = useEditWorkoutMutation();
	const [createEquipmentTrigger] = useAddGymEquipmentMutation();
	const [createCoachTrigger] = useCreateCoachMutation();
	const convertStartTime = (workoutDate, workoutTime) => {
		if (!workoutDate || !workoutTime) return null;
		const date = dayjs(workoutDate).format("YYYY-MM-DD");
		const startTime = dayjs.tz(`${date} ${workoutTime}`, "UTC").format("YYYY-MM-DD HH:mm:ss");

		return startTime;
	};

	const getPayload = async (vals) => {
		const {
			workoutCategories,
			workoutEquipment,
			workoutLength,
			workoutDate,
			workoutTime,
			workoutTimezone,
			workoutTitle,
			workoutCoach,
			workoutShortDescription,
			workoutDescription,
			...rest
		} = vals;
		const coaches = await validTrainers({
			domainGymId,
			coachesData,
			workoutCoach,
			createCoachTrigger,
		});
		const equipmentsList = await validEquipments({
			domainGymId,
			equipmentData: equipments,
			selectedEquipments: workoutEquipment,
			createEquipmentTrigger,
		});
		const payload = {
			workout_id: selectedWorkout?.id,
			categories: (workoutCategories ? workoutCategories?.join(",") : "")?.trim(),
			equipment_list: equipmentsList,
			start_time: workoutDate && workoutTime ? convertStartTime(workoutDate, workoutTime) : null,
			time_zone: workoutTimezone,
			workoutDate,
			workoutTime,
			duration: workoutLength ? workoutLength : null,
			name: encodeString(workoutTitle),
			coaches,
			description: encodeString(workoutDescription),
			short_description: encodeString(workoutShortDescription),
			...rest,
		};

		return payload;
	};
	const onFinish = async (values) => {
		const payload = await getPayload(values);
		nextStep(payload);
		await refetchTrainers();
		await refetchEquipment();
	};

	useEffect(() => {
		let timer;
		if (loading) {
			setDisabledSaveBtn(true);
			timer = setTimeout(() => {
				setDisabledSaveBtn(false);
			}, 1000);
		}
		return clearTimeout(timer);
	}, [loading]);

	const [getMuxSignedUrlTrigger, { isLoading, data: muxSignedUrl }] = useGetMuxAuthenticatedUrlMutation();

	const muxVideoUrlResponse = useGetMuxVideoUrlQuery(
		{ playbackId: currentWorkout?.playback_id },
		{ skip: !currentWorkout?.playback_id }
	);

	const [addingNewVideo, setAddingNewVideo] = useState(false);
	const [changeThumbNail, setChangeThumbnail] = useState(false);
	const handleClickNewVideo = async () => {
		setAddingNewVideo(true);
		await getMuxSignedUrlTrigger();
	};

	useEffect(() => {
		if (currentWorkout?.playback_id) {
			getMuxSignedUrlTrigger();
		}
	}, [currentWorkout?.playback_id, getMuxSignedUrlTrigger]);

	const muxDirectUploadUrl = muxSignedUrl?.url;
	const signedMuxPreviewUrl = muxVideoUrlResponse?.data?.url;
	const onSaveThumbnail = useCallback((file) => {
		const blobToFile = new File([file], "workoutThumbnail.png", {
			type: "image/png",
		});
		setThumbnailFile(blobToFile);
		setChangeThumbnail(false);
	}, []);

	const editWorkoutUploadId = async (uploadId) => {
		try {
			await editWorkoutTrigger({
				workout_id: currentWorkout.id,
				gym_id: domainGymId,
				upload_id: uploadId,
			}).unwrap();
			getWorkoutsResponse.refetch();
		} catch (error) {
			console.error("error", error);
		}
	};

	const onUploadStart = async (e) => {
		await editWorkoutUploadId(muxSignedUrl?.id);
		const file = e?.detail?.file;
		if (!file || !file?.type?.startsWith("video/")) {
			notification.error({ message: "Invalid file type" });

			return false;
		}
		return true;
	};

	// Memoize options for categories and equipment
	const categoryOptions = React.useMemo(() => {
		return categoriesData?.categories?.length > 0
			? sortByProperty(categoriesData.categories, "name").map((cat) => ({
					value: cat.name,
					id: cat.id,
			  }))
			: [];
	}, [categoriesData]);

	const equipmentOptions = React.useMemo(() => {
		return sortByProperty(equipments?.equipments || [], "name")
			?.filter(({ status }) => status === "active")
			.map((cat) => ({
				label: cat.name,
				id: cat.id,
				value: cat.id,
			}));
	}, [equipments]);

	const coachOptions = React.useMemo(() => {
		return sortByProperty(coachesData?.coaches || [], "name")
			?.filter(({ status }) => status === "active")
			?.map((coach) => ({
				label: `${decodeString(coach?.name)}`,
				value: coach?.id,
			}));
	}, [coachesData]);

	return (
		<Form
			className="workout-preview"
			initialValues={{
				workoutTitle: decodeString(values.name),
				workoutDescription: decodeString(values.description),
				workoutShortDescription: decodeString(values.short_description),
				workoutCoach: values.coaches,
				workoutIsFree: values.is_free ? true : false,
				workoutCategories: values.categories ? values.categories.split(",") : [],
				workoutEquipment: values.equipment_list,
				workoutDate: values?.start_time ? dayjs.tz(values.start_time) : null,
				workoutTime: values?.start_time ? dayjs.tz(values?.start_time).format("HH:mm") : undefined,
				workoutTimezone: values.time_zone,
				workoutDuration: durationOptions.find(({ value }) => values.duration === value)?.value || null,
				status: values.status,
			}}
			form={form}
			onFinish={onFinish}
			layout="vertical"
		>
			<Flex justify="center" style={{ marginBottom: 16 }} justifyContent="center">
				<Flex wrap style={{ maxWidth: 575 }} gap={16} justify="center" justifyContent="center">
					{preRecordedLive && (
						<Flex
							vertical
							flex={1}
							align="center"
							justify="space-between"
							className={
								!addingNewVideo || !(muxDirectUploadUrl && (addingNewVideo || !signedMuxPreviewUrl))
									? "sb-edit-workout-upload"
									: ""
							}
							style={{ minWidth: 258, minHeight: 220 }}
						>
							{isLoading && <Spin size="small" />}
							{signedMuxPreviewUrl && !addingNewVideo && (
								<VideoPlayer
									styleConfig={{
										objectFit: "cover",
										margin: "0 auto 20px auto",
										height: 136,
										width: 230,
									}}
									options={{
										autoplay: false,
										controls: true,
										errorDisplay: false,
										sources: [
											{
												src: signedMuxPreviewUrl,
											},
										],
									}}
								/>
							)}
							{muxDirectUploadUrl && addingNewVideo && (
								<MuxUploadAndVideoPlayer
									loading={false}
									endpoint={muxDirectUploadUrl}
									onSuccess={() => setAddingNewVideo(false)}
									playbackId={currentWorkout?.playback_id}
									videoUploaded={!addingNewVideo}
									onUploadStart={onUploadStart}
									onUploadAgain={handleClickNewVideo}
									style={{ width: 258, height: 220 }}
								/>
							)}
							{!addingNewVideo && (
								<Flex justify="center">
									<Button
										style={{ borderWidth: 1 }}
										className="primary-outlined--delete"
										onClick={handleClickNewVideo}
										type="button"
									>
										<EditOutlined />
										Change Workout Video
									</Button>
								</Flex>
							)}
						</Flex>
					)}

					{!changeThumbNail && (
						<Flex
							vertical
							justify="space-between"
							align="center"
							className="sb-edit-workout-upload"
							style={{ width: 258 }}
						>
							<img
								height={136}
								width={230}
								src={thumbnailFile ? URL.createObjectURL(thumbnailFile) : values?.image_url}
								alt=""
							/>
							<Flex justify="center">
								<Button
									style={{ borderWidth: 1, marginTop: 16 }}
									className="primary-outlined--delete"
									onClick={() => {
										const uploader = document.getElementById("uploader");
										if (uploader) {
											return uploader.click();
										}
										setChangeThumbnail(true);
									}}
									type="button"
								>
									<UploadOutlined />
									Change Thumbnail
								</Button>
							</Flex>
						</Flex>
					)}

					<Flex style={{ display: changeThumbNail ? "flex" : "none", width: "100%" }} align="center" vertical>
						<ImageUpload
							className="sb-edit-workout-form__upload__input"
							onSave={onSaveThumbnail}
							onCancel={() => {
								setThumbnailFile(null);
								setChangeThumbnail(false);
							}}
							type="image"
							title="Upload Thumbnail"
							containerStyles={{ width: 230, minHeight: 136, maxHeight: 136 }}
							uploaderRef={uploaderRef}
							aspectRatio={300 / 180}
							sizeLimit={{
								limit: maxImageAssetSize,
								error: imageSizeLimitError,
							}}
							id={"uploader"}
						/>
						{!thumbnailFile && <Typography.Text style={{ color: "#EB5757" }}>This field is required</Typography.Text>}
					</Flex>
				</Flex>
			</Flex>

			{!preRecordedLive && (
				<>
					<Typography.Text style={{ color: "#000" }}>Broadcast Server</Typography.Text>
					<Flex justify="space-between" className="stream-settings">
						<Typography.Text style={{ color: "#000" }}>rtmp://global-live.mux.com:5222/app</Typography.Text>
						<Typography.Text
							copyable={{
								text: "rtmp://global-live.mux.com:5222/app",
								icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
								onCopy: () => notification.success({ message: "Copied to clipboard" }),
							}}
							style={{
								color: primaryColor,
								backgroundColor: alpha(primaryColor, 0.1),
								borderRadius: "14px",
								padding: "0 8px",
							}}
						>
							Copy
						</Typography.Text>
					</Flex>
				</>
			)}
			<Row gutter={16}>
				<Col span={12}>
					<FormInput
						name="workoutTitle"
						label="Workout Title"
						rules={[
							() => ({
								required: true,
								validator(_, value) {
									const validationStatus = validateRequiredEncodedLength(value, 255);
									if (validationStatus) {
										return Promise.reject(new Error(validationStatus));
									}
									return Promise.resolve();
								},
							}),
						]}
						size="small"
						autoFocus
					/>
				</Col>
				<Col span={12}>
					{coachesData?.coaches && (
						<FormTagSelect
							name="workoutCoach"
							options={coachOptions}
							label="Workout Trainer"
							placeholder="Workout Trainer"
							rules={[
								{ required: true, message: "This field is required" },
								() => ({
									required: true,
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const longTags = value.filter((tag) => tag.length >= 256);
											if (longTags.length > 0) {
												return Promise.reject(new Error(`Maximum allowed characters is 255 per trainer.`));
											}
										}
										return Promise.resolve();
									},
								}),
								() => ({
									required: true,
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const inactiveCoaches = coachesData?.coaches
												.filter((coach) => coach.status !== "active")
												.map((coach) => coach.name.toLowerCase());
											const invalidTags = value.filter((tag) => inactiveCoaches.includes(tag.toLowerCase()));
											if (invalidTags.length > 0) {
												return Promise.reject(
													new Error(`Select inactive trainers is not allowed. (${invalidTags.join(", ")})`)
												);
											}
										}
										return Promise.resolve();
									},
								}),
							]}
							showSearch
							loading={isCoachesLoading}
						/>
					)}
				</Col>
			</Row>
			<FormTextArea
				name="workoutShortDescription"
				label="Workout Short Description"
				placeholder="Workout Short Description"
				rules={[
					() => ({
						validator(_, value) {
							const validationStatus = validateEncodedLength(value, 100);
							if (validationStatus) {
								return Promise.reject(new Error(validationStatus));
							}
							return Promise.resolve();
						},
					}),
				]}
				rows={2}
			/>
			<FormTextArea
				name="workoutDescription"
				label="Workout Description"
				placeholder="Workout Description"
				rules={[
					() => ({
						required: true,
						validator(_, value) {
							const validationStatus = validateRequiredEncodedLength(value, 2048);
							if (validationStatus) {
								return Promise.reject(new Error(validationStatus));
							}
							return Promise.resolve();
						},
					}),
				]}
				rows={4}
			/>
			<Row gutter={16}>
				<Col span={preRecordedLive ? 24 : 12}>
					{categoryOptions.length > 0 && (
						<FormTagSelect
							name="workoutCategories"
							label="Workout Categories"
							placeholder="Enter Categories"
							style={{ minHeight: 48 }}
							rules={[
								() => ({
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const longTags = value.filter((tag) => tag.length >= 31);
											if (longTags.length > 0) {
												return Promise.reject(new Error(`Maximum allowed characters is 30 per category.`));
											}
										}
										return Promise.resolve();
									},
								}),
							]}
							options={categoryOptions}
						/>
					)}
				</Col>
				{!preRecordedLive && (
					<Col span={12}>
						<FormSelect
							name="workoutDuration"
							placeholder="Workout Length"
							rules={[{ required: !preRecordedLive, message: "This field is required" }]}
							label="Workout Length"
							options={durationOptions}
							showSearch={false}
							sizes="small"
						/>
					</Col>
				)}
			</Row>
			<Flex className="date-time-zone" gap={16}>
				<FormTagSelect
					name="workoutEquipment"
					label="Workout Equipment"
					placeholder="Enter Equipment"
					mode="tags"
					style={{ minHeight: 48 }}
					rules={[
						() => ({
							validator(_, value) {
								if (value && Array.isArray(value)) {
									const longTags = value.filter((tag) => tag.length >= 256);
									if (longTags.length > 0) {
										return Promise.reject(new Error(`Maximum allowed characters is 255 per equipment.`));
									}
								}
								return Promise.resolve();
							},
						}),
						() => ({
							validator(_, value) {
								if (value && Array.isArray(value)) {
									const inactiveEquipments = equipments?.equipments
										.filter((equipment) => equipment.status !== "active")
										.map((equipment) => equipment.name.toLowerCase());
									const invalidTags = value.filter((tag) => inactiveEquipments.includes(tag.toLowerCase()));
									if (invalidTags.length > 0) {
										return Promise.reject(
											new Error(`Select inactive equipment is not allowed. (${invalidTags.join(", ")})`)
										);
									}
								}
								return Promise.resolve();
							},
						}),
					]}
					options={equipmentOptions}
				/>

				<DateTimeTimezone
					form={form}
					currentDatetime={currentDatetime}
					setCurrentDatetime={setCurrentDatetime}
					disabled={false}
					edit={selectedWorkout?.start_time && dayjs.tz(selectedWorkout?.start_time).add(2, "hours").isAfter(dayjs())}
				/>
			</Flex>

			<Flex align="center" justify="center" className="sb-workout-music-form__actions">
				<Button type="button" onClick={previousStep} className="cancel-btn" data-id="btnBack">
					Previous
				</Button>
				<Button
					loading={isDisabledSaveBtn}
					disabled={isDisabledSaveBtn}
					type="submit"
					className="sb-release-schedule-form__form__button primary-filled"
					style={{ margin: 0 }}
					data-id="btnSave"
				>
					Schedule
				</Button>
			</Flex>
		</Form>
	);
};

export default WorkoutPreview;

